import React from 'react';
import { AgCampaign } from '@indigo-ag/schema';
import {
  ColumnDefinition,
  CustomActionWithPayload,
  RowDefinition,
  Switch,
  Table,
  useFlashMessage,
  useTableState
} from '@indigo-ag/component-module';
import { useMutation, ApolloError } from '@apollo/client';

import i18next from 'core/i18n';
import { toggleAgCampaignActive } from 'features/admin/adminQueries';
import { utils } from 'shared';

export interface CampaignsListProps {
  campaigns: AgCampaign[];
}

type CampaignsColumnId = 'name' | 'salesforceId' | 'active';
type CampaignsListActions = CustomActionWithPayload<'TOGGLE_ACTIVE', number>;

const columnDefinition: ColumnDefinition<AgCampaign, CampaignsColumnId, {}, CampaignsListActions>[] = [
  {
    id: 'name',
    header: utils.text.toTitleCase(i18next.t('campaignName')),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.name} />
  },
  {
    id: 'salesforceId',
    header: 'SFDC Id',
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.salesforceId || '-'} />
  },
  {
    id: 'active',
    header: i18next.t('active', { context: 'female' }),
    cell: ({ row, onTableAction }) => (
      <Switch onChange={() => onTableAction({ type: 'TOGGLE_ACTIVE', payload: row.id })} checked={row.isActive} />
    )
  }
];

const rowDefinition: RowDefinition<AgCampaign, CampaignsColumnId> = {
  rowId: ({ row }) => row.id.toString()
};

export const CampaignsList: React.FunctionComponent<CampaignsListProps> = ({ campaigns }) => {
  /**
   * Mutations
   */
  const [toggleAgCampaignActiveFn] = useMutation(toggleAgCampaignActive);

  const { addFlashMessage } = useFlashMessage();

  const toggleActiveCampaign = (campaignId: AgCampaign['id']) => {
    toggleAgCampaignActiveFn({
      variables: {
        campaignId
      }
    })
      .then(() => {
        addFlashMessage({
          content: i18next.t('campaignUpdatedSuccessfully'),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      });
  };

  const tableState = useTableState<AgCampaign, CampaignsColumnId, CampaignsListActions>(campaigns, {
    onTableAction: action => {
      switch (action.type) {
        case 'TOGGLE_ACTIVE':
          toggleActiveCampaign(action.payload);
          return;
        default:
          return;
      }
    }
  });

  return <Table label="Campaigns" {...tableState} columnDefinition={columnDefinition} rowDefinition={rowDefinition} />;
};
